import { useEffect, useMemo, useRef } from 'react'
import {
    useBetSliderStore,
    useBuyBonusStore,
    useConfigurationStore,
    useJackpotStore,
    useSpinButtonSectionStore,
} from '@stores'
import { formatMoney } from '@utils/money'
import gsap from 'gsap'

import { useOpacityToggle, useScaleToFit } from '@hooks'

import betStepperImage from '@assets/button-betstepper-enabled-default.svg'
import betStepperImageEQ from '@assets/jackpot-eq/button-betstepper-enabled.svg'

import { BetStepperButton } from '../'
import {
    BetOptionItem,
    BetSliderSelectedIndexChangedMessage,
    BetSliderSetBetsMessage,
    BetSliderSetScrollingIndexMessage,
    BetSliderUpdateDisabledOptions,
    ChangeOpacityComponents,
    GameUIMessageScope,
    GameUIMessageType,
    OpenBetChooserClickedMessage,
} from 'game-ui-types'
import { isBetOptionItem, useActiveBetOptions } from '@utils/betOption'
import { useTranslation } from '@amusnet/translations/react'

const BetStepper = () => {
    const { t } = useTranslation()
    const currency = useConfigurationStore((s) => s.currency)
    const currencyHideCents = useConfigurationStore((s) => s.currencyHideCents)
    const centsVisibilityMoneyLimit = useConfigurationStore((s) => s.centsVisibilityMoneyLimit)
    const shouldShowCentsOnMoneyLimit = useConfigurationStore((s) => s.shouldShowCentsOnMoneyLimit)
    const isGameLoaded = useConfigurationStore((s) => s.isGameLoaded)
    const rawBetOptions = useConfigurationStore((state) => state.betOptions)

    const betGroup = useBetSliderStore((s) => s.betGroup)
    const activeBetGroupBetOptions = useBetSliderStore((s) => s.activeBetGroupBetOptions)
    const hideCentsWhenMultipleOf = useBetSliderStore((s) => s.hideCentsWhenMultipleOf)
    const activeIndex = useBetSliderStore((s) => s.activeIndex)
    const scrollToIndex = useBetSliderStore((s) => s.scrollToIndex)
    const updateScrollToIndex = useBetSliderStore((s) => s.updateScrollToIndex)
    const updateActiveIndex = useBetSliderStore((s) => s.updateActiveIndex)
    const setIsEdgeReached = useBetSliderStore((s) => s.setIsEdgeReached)
    const setActiveBetGroup = useBetSliderStore((s) => s.setActiveBetGroup)
    const setHideCentsWhenMultipleOf = useBetSliderStore((s) => s.setHideCentsWhenMultipleOf)

    const betOptions = useActiveBetOptions()

    const currentFeatureMultiplier = useBuyBonusStore((s) => s.currentFeatureMultiplier)

    const toggleBetSection = useSpinButtonSectionStore((s) => s.toggleBetSection)
    const isLongPress = useSpinButtonSectionStore((s) => s.isLongPress)
    const setActiveTriggerButtonLabel = useSpinButtonSectionStore((s) => s.setActiveTriggerButtonLabel)
    const forbiddenBetOptionIndexes = useBetSliderStore((s) => s.forbiddenBetOptionIndexes)
    const setForbiddenBetOptionIndexes = useBetSliderStore((s) => s.setForbiddenBetOptionIndexes)

    const isEQJackpotType = useJackpotStore((s) => s.isEQJackpotType)

    const styles = useConfigurationStore((s) => s.getComponentConfig('BetStepper').styles)
    const position = useConfigurationStore((s) => s.getComponentConfig('BetStepper').position)

    const betOptionsContainerRef = useRef<HTMLDivElement>(null)
    const betAmountContainerRef = useRef<HTMLDivElement>(null)
    const betAmountContentRef = useRef<HTMLDivElement>(null)
    const betStepperComponent = useRef<HTMLDivElement>(null)

    const itemWidth = 156

    useOpacityToggle(ChangeOpacityComponents.BET_STEPPER, betStepperComponent)

    const slideToIndex = (index: number, isInstant = false) => {
        const container = betOptionsContainerRef.current

        if (!container) return

        setIsEdgeReached(index === 0 || index === betOptions.length - 1)
        updateScrollToIndex(index)

        const targetX = -index * itemWidth

        gsap.killTweensOf(container)

        if (isInstant) {
            gsap.set(container, { x: targetX })
        } else {
            gsap.to(container, {
                x: targetX,
                duration: 1,
                ease: 'elastic.out(1, 1.5)',
            })
        }

        updateActiveIndex(index)
    }

    useEffect(() => {
        if (!isGameLoaded || scrollToIndex === -1 || scrollToIndex === activeIndex) return

        slideToIndex(scrollToIndex, false)
    }, [scrollToIndex])

    useEffect(() => {
        if (activeIndex === scrollToIndex) return

        slideToIndex(activeIndex, true)
    }, [activeIndex])

    useEffect(() => {
        const selectedIndexChangedMessage: BetSliderSelectedIndexChangedMessage = {
            scope: GameUIMessageScope.BET_SLIDER,
            type: GameUIMessageType.BET_SLIDER_SELECTED_INDEX_CHANGED,
            isScrolling: false,
            index: activeBetGroupBetOptions ? activeBetGroupBetOptions[activeIndex]?.betIndex : activeIndex,
        }

        window.postMessage(selectedIndexChangedMessage)
    }, [activeIndex, activeBetGroupBetOptions])

    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            const rawData = event.data
            const type = rawData.type
            const scope = rawData.scope

            if (scope !== GameUIMessageScope.MAIN_BUTTONS) {
                return
            }

            switch (type) {
                case GameUIMessageType.BET_SLIDER_SET_SCROLLING_INDEX: {
                    const data = rawData as BetSliderSetScrollingIndexMessage

                    if (betGroup) {
                        const item = rawBetOptions[data.currentBetIndex] as BetOptionItem
                        const index = (betOptions as BetOptionItem[]).findIndex((option) => option === item)

                        setActiveBetGroup(item.betGroupId)
                        slideToIndex(index)
                    } else {
                        slideToIndex(data.currentBetIndex, true)
                    }

                    break
                }
                case GameUIMessageType.BET_SLIDER_SET_BETS: {
                    const data = rawData as BetSliderSetBetsMessage

                    setForbiddenBetOptionIndexes(data.isForbidden ? [...Array(betOptions.length).keys()] : [])
                    setHideCentsWhenMultipleOf(data.hideCentsWhenMultipleOf)
                    break
                }
                case GameUIMessageType.BET_SLIDER_UPDATE_DISABLED_OPTIONS: {
                    const data = rawData as BetSliderUpdateDisabledOptions

                    setForbiddenBetOptionIndexes(data.forbiddenIndexes)
                    break
                }
                default:
                    break
            }
        }

        window.addEventListener('message', handleMessage, false)

        return () => {
            window.removeEventListener('message', handleMessage, false)
        }
    }, [])

    const scale = useScaleToFit(betAmountContainerRef, betAmountContentRef, [
        scrollToIndex,
        activeIndex,
        currentFeatureMultiplier,
        activeBetGroupBetOptions,
    ])

    const renderBetOptions = useMemo(() => {
        const renderBetOption = (betOption: number | BetOptionItem, index: number) => {
            const color = forbiddenBetOptionIndexes?.includes(betGroup ? (betOption as BetOptionItem).betIndex : index)
                ? '#E8201D'
                : undefined
            const isBetGrouped = isBetOptionItem(betOption)
            const betValue = (isBetGrouped ? betOption.betAmount : betOption) * currentFeatureMultiplier
            const key = isBetGrouped ? `${betOption.betGroupId}-${betValue}` : betValue
            const shouldHideCents =
                currencyHideCents ||
                (betValue > centsVisibilityMoneyLimit &&
                    !shouldShowCentsOnMoneyLimit &&
                    Boolean(hideCentsWhenMultipleOf && betValue % hideCentsWhenMultipleOf === 0))

            return (
                <div key={key} className="flex justify-center items-center" style={{ width: `${itemWidth}px` }}>
                    <div
                        ref={index === scrollToIndex ? betAmountContentRef : null}
                        className="flex justify-center items-center text-nowrap whitespace-nowrap"
                        style={{ transform: `scale(${scale})`, color }}
                    >
                        {formatMoney(betValue, shouldHideCents)}
                    </div>
                </div>
            )
        }

        return betOptions.map((betOption, index) => renderBetOption(betOption, index))
    }, [
        scale,
        betOptions,
        centsVisibilityMoneyLimit,
        currentFeatureMultiplier,
        currencyHideCents,
        scrollToIndex,
        isGameLoaded,
        forbiddenBetOptionIndexes,
    ])

    const handlePrevClicked = () => slideToIndex(Math.max(scrollToIndex - 1, 0))
    const handleNextClicked = () => slideToIndex(Math.min(scrollToIndex + 1, betOptions.length - 1))
    const handleBetStepperClicked = () => {
        const openChooserClickedMessage: OpenBetChooserClickedMessage = {
            type: GameUIMessageType.OPEN_BET_CHOOSER_CLICKED,
        }

        window.postMessage(openChooserClickedMessage, '*')

        setActiveTriggerButtonLabel('betStepper')

        toggleBetSection()
    }

    const isMin = scrollToIndex === 0
    const isMax = scrollToIndex === betOptions.length - 1

    return (
        <div
            id="bet-stepper"
            ref={betStepperComponent}
            className={`${styles} flex items-center`}
            style={{ ...position }}
        >
            <BetStepperButton
                type="minus"
                disabled={isMin || isLongPress}
                onClick={handlePrevClicked}
                className="mr-[10px]"
            />
            <div
                className="flex flex-col justify-center items-center cursor-pointer relative"
                style={{
                    backgroundImage: `url("${!isEQJackpotType ? betStepperImage : betStepperImageEQ}")`,
                    width: `${itemWidth}px`,
                    height: '86px',
                    backgroundSize: 'contain',
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'center',
                    pointerEvents: isLongPress ? 'none' : 'auto',
                }}
                onClick={handleBetStepperClicked}
            >
                {!isEQJackpotType ? (
                    <>
                        <div className="absolute left-[3px] top-[24px] w-[24px] h-[40px] rounded-[50%] bg-gradient-to-l from-transparent to-black pointer-events-none z-[20]" />
                        <div className="absolute right-[3px] top-[24px] w-[24px] h-[40px] rounded-[50%] bg-gradient-to-r from-transparent to-black pointer-events-none z-[20]" />
                    </>
                ) : (
                    ''
                )}
                <div
                    className="text-[#CACACA] text-[20px] font-[700] leading-none"
                    style={{
                        paddingTop: !isEQJackpotType ? `4px` : '7px',
                    }}
                >
                    {t('gameui.bet')}:
                </div>
                <div
                    ref={betAmountContainerRef}
                    className="relative overflow-hidden w-[138px] h-[86px] flex flex-row justify-center items-center rounded-[20px] "
                >
                    <div
                        ref={betOptionsContainerRef}
                        style={{ width: `${betOptions.length * itemWidth}px` }}
                        className="absolute text-[44px] left-[-10px] text-white font-bold flex "
                    >
                        {renderBetOptions}
                    </div>
                </div>
                <div
                    className="text-[#CACACA] text-[20px] font-[700] leading-none"
                    style={{
                        paddingBottom: !isEQJackpotType ? '1px' : '4px',
                    }}
                >
                    {currency}
                </div>
            </div>

            <BetStepperButton
                type="plus"
                disabled={isMax || isLongPress}
                onClick={handleNextClicked}
                className="ml-[10px]"
            />
        </div>
    )
}

export default BetStepper
